import { useRef, useState } from "react";
import { useCrm } from "@/lib/crm";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AgendaDialog } from "@/components/AgendaDialog";
import { AgendaRow, useAgenda } from "@/pages/Agenda";
import { eur2, dateIt } from "@/lib/constants";
import { toast } from "sonner";
import { Plus, Trash2, Upload, Download, FileText, CheckCircle2, Circle } from "lucide-react";

const nid = () => `n-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;

export function DiarioTab({ p }) {
  const { items, save, remove, toggle } = useAgenda(p.id);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const sorted = [...items].sort((a, b) => (b.data + (b.ora || "")).localeCompare(a.data + (a.ora || "")));
  return (
    <div className="space-y-2" data-testid="portfolio-diario">
      <div className="flex items-center justify-between">
        <p className="text-xs text-slate-500">Colloqui, chiamate e appuntamenti con questa società. Privato: lo vedi solo tu.</p>
        <Button data-testid="diario-add-button" size="sm" variant="outline" className="rounded-full gap-1 border-dashed" onClick={() => { setEditing(null); setOpen(true); }}><Plus className="h-3.5 w-3.5" /> Nuova voce</Button>
      </div>
      {sorted.length === 0 && <p className="text-sm text-slate-400 py-4 text-center">Nessuna voce nel diario.</p>}
      {sorted.map((a) => (
        <div key={a.id}>
          <AgendaRow a={a} onOpen={() => { setEditing(a); setOpen(true); }} onToggle={toggle} />
          {(a.resoconto || a.prossimo_passo) && (
            <div className="ml-10 mt-1 mb-2 text-xs text-slate-500 whitespace-pre-line">
              {a.resoconto}
              {a.prossimo_passo && <div className="text-primary mt-0.5">→ {a.prossimo_passo}{a.prossimo_data ? ` (${dateIt(a.prossimo_data)})` : ""}</div>}
            </div>
          )}
        </div>
      ))}
      <AgendaDialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }} initial={editing} fixedPortfolio={{ id: p.id, nome: p.nome }}
        onSave={async (d) => { await save(editing, d); setOpen(false); setEditing(null); }}
        onDelete={async (a) => { if (await remove(a)) { setOpen(false); setEditing(null); } }} />
    </div>
  );
}

export function IncassiTab({ incassi, setIncassi, ricavi }) {
  const tot = incassi.reduce((a, x) => a + (Number(x.importo) || 0), 0);
  const set = (i, k, v) => setIncassi(incassi.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  return (
    <div className="space-y-3" data-testid="portfolio-incassi">
      <div className="grid grid-cols-3 gap-3">
        {[["Totale lavori", ricavi, "text-sky-600"], ["Incassato", tot, "text-emerald-600"], ["Da incassare", ricavi - tot, ricavi - tot > 0 ? "text-rose-600" : "text-slate-500"]].map(([l, v, c]) => (
          <div key={l} className="rounded-lg bg-slate-50 dark:bg-slate-900/50 p-3"><div className="text-[10px] uppercase tracking-wider text-slate-400">{l}</div><div data-testid={`incassi-${l.toLowerCase().replace(/ /g, "-")}`} className={`text-lg font-bold tabular-nums ${c}`}>{eur2(v)}</div></div>
        ))}
      </div>
      {incassi.map((x, i) => (
        <div key={x.id || i} data-testid={`incasso-row-${i}`} className="flex items-center gap-2">
          <Input type="date" value={x.data || ""} onChange={(e) => set(i, "data", e.target.value)} className="w-36 h-9" />
          <Select value={x.tipo || "Acconto"} onValueChange={(v) => set(i, "tipo", v)}>
            <SelectTrigger className="w-28 h-9"><SelectValue /></SelectTrigger>
            <SelectContent>{["Acconto", "Saldo", "Rata"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
          <Input value={x.note || ""} onChange={(e) => set(i, "note", e.target.value)} placeholder="Note (es. bonifico)" className="flex-1 h-9" />
          <Input data-testid={`incasso-importo-${i}`} type="number" value={x.importo} onChange={(e) => set(i, "importo", e.target.value)} className="w-28 h-9 text-right tabular-nums" />
          <button onClick={() => setIncassi(incassi.filter((_, j) => j !== i))} className="text-slate-300 hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
        </div>
      ))}
      <Button data-testid="incasso-add-button" variant="outline" className="rounded-full gap-1.5 border-dashed" onClick={() => setIncassi([...incassi, { id: nid(), data: new Date().toISOString().slice(0, 10), tipo: "Acconto", importo: 0, note: "" }])}><Plus className="h-4 w-4" /> Aggiungi incasso</Button>
      <p className="text-[11px] text-slate-400">"Totale lavori" è la somma di Prodotti/Servizi. Ricorda di premere Salva.</p>
    </div>
  );
}

export function LavoriTab({ lavori, setLavori }) {
  const { settings } = useCrm();
  const tipi = settings?.tipi_lavorazione || [];
  const [nuovo, setNuovo] = useState("");
  const set = (i, k, v) => setLavori(lavori.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  const add = (tipo) => tipo.trim() && setLavori([...lavori, { id: nid(), tipo: tipo.trim(), fatto: false, scadenza: "", note: "" }]);
  const done = lavori.filter((x) => x.fatto).length;
  return (
    <div className="space-y-3" data-testid="portfolio-lavori">
      <div className="text-xs text-slate-500">Completati <b className="text-slate-900 dark:text-white">{done}/{lavori.length}</b> · Lista privata, la vedi solo tu.</div>
      {lavori.map((x, i) => (
        <div key={x.id || i} data-testid={`lavoro-row-${i}`} className="flex items-center gap-2">
          <button data-testid={`lavoro-toggle-${i}`} onClick={() => set(i, "fatto", !x.fatto)}>{x.fatto ? <CheckCircle2 className="h-5 w-5 text-emerald-500" /> : <Circle className="h-5 w-5 text-slate-300 hover:text-primary" />}</button>
          <span className={`w-40 text-sm font-medium truncate ${x.fatto ? "line-through text-slate-400" : ""}`}>{x.tipo}</span>
          <Input value={x.note || ""} onChange={(e) => set(i, "note", e.target.value)} placeholder="Note" className="flex-1 h-9" />
          <Input type="date" value={x.scadenza || ""} onChange={(e) => set(i, "scadenza", e.target.value)} className="w-36 h-9" title="Scadenza" />
          <button onClick={() => setLavori(lavori.filter((_, j) => j !== i))} className="text-slate-300 hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
        </div>
      ))}
      <div className="flex flex-wrap gap-1.5">
        {tipi.map((t) => (
          <button key={t} data-testid={`lavoro-quick-${t}`} onClick={() => add(t)} className="text-xs px-2.5 py-1 rounded-full border border-slate-200 dark:border-slate-700 hover:border-primary hover:text-primary transition-colors">+ {t}</button>
        ))}
      </div>
      <div className="flex gap-2">
        <Input data-testid="lavoro-nuovo-input" value={nuovo} onChange={(e) => setNuovo(e.target.value)} placeholder="Altro tipo di lavorazione…" className="h-9" onKeyDown={(e) => e.key === "Enter" && (add(nuovo), setNuovo(""))} />
        <Button data-testid="lavoro-add-button" variant="outline" size="sm" className="rounded-full gap-1" onClick={() => { add(nuovo); setNuovo(""); }}><Plus className="h-3.5 w-3.5" /> Aggiungi</Button>
      </div>
      <p className="text-[11px] text-slate-400">I tipi rapidi si modificano in Impostazioni. Ricorda di premere Salva.</p>
    </div>
  );
}

export function DocumentiTab({ p, readOnly }) {
  const { api, refresh } = useCrm();
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const docs = p.documenti || [];
  const upload = async (files) => {
    setBusy(true);
    try {
      for (const f of files) await api.uploadPortfolioDoc(p.id, f);
      await refresh();
      toast.success("Documento caricato");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nel caricamento");
    } finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };
  const del = async (d) => {
    if (!window.confirm(`Eliminare "${d.nome}"?`)) return;
    await api.deletePortfolioDoc(p.id, d.id);
    await refresh();
    toast.success("Documento eliminato");
  };
  return (
    <div className="space-y-2" data-testid="portfolio-documenti">
      {!readOnly && (
        <>
          <input ref={ref} type="file" multiple className="hidden" data-testid="documento-file-input" onChange={(e) => e.target.files?.length && upload([...e.target.files])} />
          <Button data-testid="documento-upload-button" variant="outline" disabled={busy} className="rounded-full gap-1.5 border-dashed" onClick={() => ref.current?.click()}><Upload className="h-4 w-4" /> {busy ? "Caricamento…" : "Carica documento"}</Button>
        </>
      )}
      {docs.length === 0 && <p className="text-sm text-slate-400 py-4 text-center">Nessun documento.</p>}
      {docs.map((d) => (
        <div key={d.id} data-testid={`documento-${d.id}`} className="flex items-center gap-3 rounded-lg border border-slate-200 dark:border-slate-800 p-3">
          <FileText className="h-4 w-4 text-primary" />
          <div className="flex-1 min-w-0"><div className="text-sm font-medium truncate">{d.nome}</div><div className="text-xs text-slate-400">{dateIt((d.uploaded_at || "").slice(0, 10))} · {Math.max(1, Math.round((d.size || 0) / 1024))} KB</div></div>
          <button data-testid={`documento-download-${d.id}`} onClick={() => api.downloadPortfolioDoc(p.id, d)} className="text-slate-400 hover:text-primary"><Download className="h-4 w-4" /></button>
          {!readOnly && <button data-testid={`documento-delete-${d.id}`} onClick={() => del(d)} className="text-slate-300 hover:text-destructive"><Trash2 className="h-4 w-4" /></button>}
        </div>
      ))}
    </div>
  );
}
